import { FileCsv, FileXls, UploadSimple, X } from '@phosphor-icons/react'
import { useRef, useState, type ChangeEvent, type DragEvent, type KeyboardEvent } from 'react'
import type { SourceType } from '../../types'

export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024

export interface ImportedFileSummary {
  fileName: string
  fileSize: number
  sheetCount: number
}

interface FileDropZoneProps {
  busy: boolean
  error: string | null
  file: ImportedFileSummary | null
  onClear: () => void
  onFile: (file: File) => void
  sourceType: SourceType
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function acceptsFile(file: File): boolean {
  const lowerName = file.name.toLocaleLowerCase()
  return lowerName.endsWith('.xlsx') || lowerName.endsWith('.csv')
}

export function FileDropZone({
  busy,
  error,
  file,
  onClear,
  onFile,
  sourceType,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const title = sourceType === 'baseline' ? '基准版' : '对比版'
  const caption = sourceType === 'baseline' ? '原始或前一版清单' : '修订或后一版清单'

  const choose = (candidate: File | undefined): void => {
    if (!candidate) return
    if (!acceptsFile(candidate)) {
      setLocalError('仅支持 .xlsx 和 .csv 文件。旧版 .xls 请先另存为 .xlsx。')
      return
    }
    if (candidate.size > MAX_FILE_SIZE_BYTES) {
      setLocalError('文件超过 25MB 上限，请拆分工作簿或精简后重试。')
      return
    }
    setLocalError(null)
    onFile(candidate)
  }

  const handleInput = (event: ChangeEvent<HTMLInputElement>): void => {
    choose(event.target.files?.[0])
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    setDragging(false)
    choose(event.dataTransfer.files[0])
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      inputRef.current?.click()
    }
  }

  return (
    <section className="file-slot" aria-labelledby={`${sourceType}-file-title`}>
      <div className="file-slot__heading">
        <div>
          <span className={`source-chip source-chip--${sourceType}`}>{title}</span>
          <h3 id={`${sourceType}-file-title`}>{caption}</h3>
        </div>
        {file ? (
          <button
            className="icon-button"
            type="button"
            onClick={onClear}
            aria-label={`移除${title}`}
          >
            <X aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {file ? (
        <div className="file-summary" data-testid={`${sourceType}-file-summary`}>
          <span className="file-summary__icon">
            {file.fileName.toLocaleLowerCase().endsWith('.csv') ? (
              <FileCsv aria-hidden="true" weight="duotone" />
            ) : (
              <FileXls aria-hidden="true" weight="duotone" />
            )}
          </span>
          <div>
            <strong title={file.fileName}>{file.fileName}</strong>
            <span>
              {formatBytes(file.fileSize)} · {file.sheetCount} 个工作表
            </span>
          </div>
          {busy ? <span className="processing-label">正在解析结构</span> : null}
        </div>
      ) : (
        <div
          className="file-drop-zone"
          data-dragging={dragging}
          data-testid={`${sourceType}-drop-zone`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={handleKeyDown}
          onDragEnter={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (event.currentTarget === event.target) setDragging(false)
          }}
          onDrop={handleDrop}
          aria-describedby={`${sourceType}-drop-help`}
        >
          <UploadSimple aria-hidden="true" weight="duotone" />
          <strong>{busy ? '正在读取文件' : '拖放文件到这里，或点击选择'}</strong>
          <span id={`${sourceType}-drop-help`}>支持 .xlsx、.csv，最大 25MB</span>
          <input
            ref={inputRef}
            className="sr-only"
            type="file"
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            onChange={handleInput}
            tabIndex={-1}
          />
        </div>
      )}

      {localError || error ? (
        <p className="inline-message inline-message--error" role="alert">
          {localError ?? error}
        </p>
      ) : null}
    </section>
  )
}
